// src/api.ts
import {
  makeAuthHeaders,
  makeUrl,
  RustyDeposit,
  RustyReferral,
  RustySecurityLog,
  RustySubscription
} from "@gofranz/common";
import axios from "axios";
function isConcurrencyLimitError(value) {
  if (typeof value !== "object" || value === null) return false;
  const v = value;
  return v.error === "concurrent_streams_limit" && typeof v.message_user_facing === "string" && typeof v.retry_after_seconds === "number" && Number.isFinite(v.retry_after_seconds) && v.retry_after_seconds >= 0 && typeof v.current_streams === "number" && Number.isFinite(v.current_streams) && v.current_streams >= 0 && typeof v.max_streams === "number" && Number.isFinite(v.max_streams) && v.max_streams >= 0;
}
var GptMuseApi = class {
  constructor({ baseUrl, timeout, auth, errorHandler, successHandler }) {
    this.getAuthApi = () => {
      if (!this.auth) {
        throw new Error("Auth config not set");
      }
      return this.auth;
    };
    this.getDepositApi = () => new RustyDeposit({ client: this.client });
    this.getSubscriptionApi = () => new RustySubscription({ client: this.client });
    this.getReferralApi = () => new RustyReferral({ client: this.client });
    this.getSecurityLogApi = () => new RustySecurityLog({ client: this.client });
    this.getAccessToken = () => this.getAuthApi().getAccessToken();
    this.getAuthHeaders = () => makeAuthHeaders(this.getAccessToken());
    this.getAxiosConfig = () => ({ headers: this.getAuthHeaders(), timeout: this.timeout });
    this.getAccountBalance = async () => this.getDepositApi().getAccountBalance(this.getAccessToken());
    this.getAccountMovements = async (query = {}) => this.getDepositApi().getAccountMovements(query, this.getAccessToken());
    this.getDeposits = async (query) => this.getDepositApi().getDeposits(query, this.getAccessToken());
    this.getDeposit = async (id) => this.getDepositApi().getDeposit(id, this.getAccessToken());
    this.newDeposit = async (deposit) => this.getDepositApi().newDeposit(deposit, this.getAccessToken());
    // Subscription endpoints
    this.getSubscriptionPlans = async () => this.getSubscriptionApi().getSubscriptionPlans();
    this.getCurrentSubscription = async () => this.getSubscriptionApi().getCurrentSubscription(this.getAccessToken());
    this.subscribeToplan = async (planId) => this.getSubscriptionApi().subscribeToplan(planId, this.getAccessToken());
    this.cancelSubscription = async () => this.getSubscriptionApi().cancelSubscription(this.getAccessToken());
    this.getSubscriptionUsage = async () => this.getSubscriptionApi().getSubscriptionUsage(this.getAccessToken());
    this.createCustomerPortalSession = async () => this.getSubscriptionApi().createCustomerPortalSession(this.getAccessToken());
    // Referral endpoints
    this.getReferralCode = async () => this.getReferralApi().getReferralCode(this.getAccessToken());
    this.getReferralStats = async () => this.getReferralApi().getReferralStats(this.getAccessToken());
    this.getReferralHistory = async () => this.getReferralApi().getReferralHistory(this.getAccessToken());
    // Security log (user-facing audit trail — GET /v1/a/audit)
    this.getSecurityLog = async (query = {}) => this.getSecurityLogApi().getSecurityLog(query, this.getAccessToken());
    // Email verification endpoints
    this.newVerifiedEmail = async (data) => {
      await this.client.post("/a/emails/verify", data, this.getAxiosConfig());
    };
    this.verifyVerifiedEmail = async (id) => {
      await this.client.post("/a/emails/verify/retry", { verified_email_id: id });
    };
    this.deleteVerifiedEmail = async (id) => {
      await this.client.delete(`/a/emails/${id}`, this.getAxiosConfig());
    };
    this.getVerifiedEmails = async () => {
      const response = await this.client.get("/a/emails");
      return response.data;
    };
    // Chat completions endpoints
    this.chatCompletions = async (data) => {
      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream"
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          throw new Error(`Chat completion failed: ${response.statusText}`);
        }
        if (response.status === 429 && isConcurrencyLimitError(errorData)) {
          throw new Error(JSON.stringify(errorData));
        }
        if (errorData.error_code) {
          throw new Error(JSON.stringify(errorData));
        }
        const errorMessage = errorData.message || errorData.error || `Chat completion failed: ${response.statusText}`;
        throw new Error(errorMessage);
      }
      return {
        stream: response.body,
        headers: response.headers
      };
    };
    this.chatCompletionsAuthenticated = async (data) => {
      const response = await fetch(`${this.baseUrl}/a/chat/completions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          Authorization: `Bearer ${this.getAccessToken()}`
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          throw new Error(`Authenticated chat completion failed: ${response.statusText}`);
        }
        if (response.status === 429 && isConcurrencyLimitError(errorData)) {
          throw new Error(JSON.stringify(errorData));
        }
        if (errorData.error_code) {
          throw new Error(JSON.stringify(errorData));
        }
        const errorMessage = errorData.message || errorData.error || `Authenticated chat completion failed: ${response.statusText}`;
        throw new Error(errorMessage);
      }
      return {
        stream: response.body,
        headers: response.headers
      };
    };
    // Models endpoints
    this.listModels = async (query) => {
      const response = await this.client.get(makeUrl("/models", query));
      return response.data;
    };
    this.listProvidersForModel = async (modelId, query) => {
      const response = await this.client.get(
        makeUrl(`/models/${modelId}/providers`, query)
      );
      return response.data;
    };
    // Rate limit endpoints
    this.getRateLimitStatus = async () => {
      const response = await fetch(`${this.baseUrl}/rate-limit/status`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        }
      });
      if (!response.ok) {
        throw new Error(`Failed to get rate limit status: ${response.statusText}`);
      }
      return await response.json();
    };
    // Characters endpoints
    this.listCharacters = async () => {
      const response = await this.client.get(makeUrl("/characters"));
      return response.data;
    };
    // Two-Factor Authentication endpoints
    this.getTwoFactorStatus = async () => {
      const response = await this.client.get("/a/2fa/status");
      return response.data;
    };
    this.setupTotp = async (name) => {
      const response = await this.client.post("/a/2fa/setup/totp", {
        name: name || "Authenticator"
      });
      return response.data;
    };
    this.verifyTotpSetup = async (setupToken, code) => {
      const response = await this.client.post("/a/2fa/setup/totp/verify", {
        setup_token: setupToken,
        code
      });
      return response.data;
    };
    this.disableTwoFactorMethod = async (methodId, code) => {
      await this.client.delete(`/a/2fa/methods/${methodId}`, {
        ...this.getAxiosConfig(),
        data: { code }
      });
    };
    this.getRecoveryCodesCount = async () => {
      const response = await this.client.get("/a/2fa/recovery-codes");
      return response.data;
    };
    this.regenerateRecoveryCodes = async (code) => {
      const response = await this.client.post(
        "/a/2fa/recovery-codes/regenerate",
        { code }
      );
      return response.data;
    };
    // Account data export & deletion
    this.exportAccountData = async () => {
      const response = await this.client.get("/a/account/export", { timeout: 12e4 });
      return response.data;
    };
    this.deleteAccount = async (confirmation) => {
      const response = await this.client.delete("/a/account", {
        ...this.getAxiosConfig(),
        data: { confirmation }
      });
      return response.data;
    };
    if (baseUrl) {
      this.baseUrl = baseUrl;
    } else {
      this.baseUrl = "https://api.gpt-muse.com/v1";
    }
    console.info(`[api] => baseUrl: ${this.baseUrl}`);
    if (timeout) {
      this.timeout = timeout;
    } else {
      this.timeout = 5e3;
    }
    if (auth) {
      this.auth = auth;
    } else {
      console.warn("Auth config not set");
    }
    this.errorHandler = errorHandler;
    this.successHandler = successHandler;
    this.client = axios.create({
      baseURL: this.baseUrl,
      timeout: this.timeout,
      headers: {
        "Content-Type": "application/json"
      }
    });
    this.client.interceptors.request.use(async (config) => {
      const needsAuth = config.url?.startsWith("/a/");
      if (needsAuth && this.auth) {
        if (!this.auth.hasValidAccessToken()) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
        const token = this.auth.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      return config;
    });
    this.client.interceptors.response.use(
      (response) => {
        if (this.successHandler) {
          this.successHandler(response);
        }
        return response;
      },
      // Pass through successful responses
      (error) => {
        if (this.errorHandler) {
          this.errorHandler(error);
        }
        return Promise.reject(error);
      }
    );
  }
};

// src/types/generated.ts
var MessageRole = /* @__PURE__ */ ((MessageRole2) => {
  MessageRole2["System"] = "System";
  MessageRole2["User"] = "User";
  MessageRole2["Assistant"] = "Assistant";
  MessageRole2["Tool"] = "Tool";
  return MessageRole2;
})(MessageRole || {});
var RegionPreference = /* @__PURE__ */ ((RegionPreference2) => {
  RegionPreference2["EU"] = "EU";
  RegionPreference2["US"] = "US";
  RegionPreference2["Asia"] = "Asia";
  return RegionPreference2;
})(RegionPreference || {});
var BaseModelId = /* @__PURE__ */ ((BaseModelId2) => {
  BaseModelId2["ClaudeOpus41"] = "claude-opus-4.1";
  BaseModelId2["Claude35Sonnet"] = "claude-3.5-sonnet";
  BaseModelId2["ClaudeSonnet4"] = "claude-sonnet-4";
  BaseModelId2["Claude37SonnetThinking"] = "claude-3.7-sonnet-thinking";
  BaseModelId2["GptOss120b"] = "gpt-oss-120b";
  BaseModelId2["Gpt4o"] = "gpt-4o";
  BaseModelId2["Gpt4oMini"] = "gpt-4o-mini";
  BaseModelId2["Gpt5Mini"] = "gpt-5-mini";
  BaseModelId2["Gpt5Nano"] = "gpt-5-nano";
  BaseModelId2["Gpt5Image"] = "gpt-5-image";
  BaseModelId2["Gpt5ImageMini"] = "gpt-5-image-mini";
  BaseModelId2["Gpt51"] = "gpt-5.1";
  BaseModelId2["Gpt51Chat"] = "gpt-5.1-chat";
  BaseModelId2["Gpt51Codex"] = "gpt-5.1-codex";
  BaseModelId2["Grok4"] = "grok-4";
  BaseModelId2["Grok3Mini"] = "grok-3-mini";
  BaseModelId2["Gemini15Pro"] = "gemini-1.5-pro";
  BaseModelId2["Gemini20Flash"] = "gemini-2.0-flash";
  BaseModelId2["Gemini25Flash"] = "gemini-2.5-flash";
  BaseModelId2["Gemini25Pro"] = "gemini-2.5-pro";
  BaseModelId2["Gemini25FlashLite"] = "gemini-2.5-flash-lite";
  BaseModelId2["Gemini25FlashImagePreview"] = "gemini-2.5-flash-image-preview";
  BaseModelId2["Gemini25FlashImage"] = "gemini-2.5-flash-image";
  BaseModelId2["GptOss20b"] = "gpt-oss-20b";
  BaseModelId2["DolphinMistral24bVeniceEdition"] = "dolphin-mistral-24b-venice-edition";
  BaseModelId2["Llama3370bInstruct"] = "llama-3.3-70b-instruct";
  BaseModelId2["DeepSeekR1"] = "deepseek-r1";
  BaseModelId2["KimiK2"] = "kimi-k2";
  BaseModelId2["ClaudeOpus45"] = "claude-opus-4.5";
  BaseModelId2["ClaudeSonnet45"] = "claude-sonnet-4.5";
  BaseModelId2["ClaudeHaiku45"] = "claude-haiku-4.5";
  BaseModelId2["Gpt52"] = "gpt-5.2";
  BaseModelId2["Gpt52Pro"] = "gpt-5.2-pro";
  BaseModelId2["O3"] = "o3";
  BaseModelId2["O3Mini"] = "o3-mini";
  BaseModelId2["Gemini3ProPreview"] = "gemini-3-pro-preview";
  BaseModelId2["Gemini3FlashPreview"] = "gemini-3-flash-preview";
  BaseModelId2["ClaudeOpus47"] = "claude-opus-4.7";
  BaseModelId2["ClaudeSonnet46"] = "claude-sonnet-4.6";
  BaseModelId2["Gpt55"] = "gpt-5.5";
  BaseModelId2["Gpt55Pro"] = "gpt-5.5-pro";
  BaseModelId2["Gpt54Mini"] = "gpt-5.4-mini";
  BaseModelId2["Gpt54Nano"] = "gpt-5.4-nano";
  BaseModelId2["Gpt54Image2"] = "gpt-5.4-image-2";
  BaseModelId2["Gpt53Codex"] = "gpt-5.3-codex";
  BaseModelId2["Gpt53Chat"] = "gpt-5.3-chat";
  BaseModelId2["Gemini31ProPreview"] = "gemini-3.1-pro-preview";
  BaseModelId2["Gemini31FlashImagePreview"] = "gemini-3.1-flash-image-preview";
  BaseModelId2["Gemini31FlashLite"] = "gemini-3.1-flash-lite";
  BaseModelId2["Grok43"] = "grok-4.3";
  BaseModelId2["Llama4Maverick"] = "llama-4-maverick";
  BaseModelId2["KimiK26"] = "kimi-k2.6";
  BaseModelId2["DeepSeekV4Pro"] = "deepseek-v4-pro";
  BaseModelId2["O4Mini"] = "o4-mini";
  BaseModelId2["GptOss120bFree"] = "gpt-oss-120b-free";
  BaseModelId2["GptOss20bFree"] = "gpt-oss-20b-free";
  BaseModelId2["DolphinMistral24bVeniceEditionFree"] = "dolphin-mistral-24b-venice-edition-free";
  BaseModelId2["Llama3370bInstructFree"] = "llama-3.3-70b-instruct-free";
  BaseModelId2["DeepSeekR1Free"] = "deepseek-r1-free";
  BaseModelId2["KimiK2Free"] = "kimi-k2-free";
  return BaseModelId2;
})(BaseModelId || {});
var ModelCapability = /* @__PURE__ */ ((ModelCapability2) => {
  ModelCapability2["Text"] = "Text";
  ModelCapability2["Vision"] = "Vision";
  ModelCapability2["Tools"] = "Tools";
  ModelCapability2["StructuredOutput"] = "StructuredOutput";
  ModelCapability2["Streaming"] = "Streaming";
  ModelCapability2["WebSearch"] = "WebSearch";
  ModelCapability2["ImageGeneration"] = "ImageGeneration";
  return ModelCapability2;
})(ModelCapability || {});
var ProviderType = /* @__PURE__ */ ((ProviderType2) => {
  ProviderType2["Anthropic"] = "Anthropic";
  ProviderType2["OpenAI"] = "OpenAI";
  ProviderType2["Google"] = "Google";
  ProviderType2["Azure"] = "Azure";
  ProviderType2["OpenRouter"] = "OpenRouter";
  return ProviderType2;
})(ProviderType || {});
var ReasoningFormat = /* @__PURE__ */ ((ReasoningFormat2) => {
  ReasoningFormat2["Effort"] = "Effort";
  ReasoningFormat2["MaxTokens"] = "MaxTokens";
  return ReasoningFormat2;
})(ReasoningFormat || {});
var ReasoningSupport = /* @__PURE__ */ ((ReasoningSupport2) => {
  ReasoningSupport2["None"] = "None";
  ReasoningSupport2["Mandatory"] = "Mandatory";
  ReasoningSupport2["Optional"] = "Optional";
  return ReasoningSupport2;
})(ReasoningSupport || {});
export {
  BaseModelId,
  GptMuseApi,
  MessageRole,
  ModelCapability,
  ProviderType,
  ReasoningFormat,
  ReasoningSupport,
  RegionPreference,
  isConcurrencyLimitError
};
//# sourceMappingURL=index.js.map